/**
 * ReservationImageGallery - 다중 이미지 갤러리 컴포넌트
 * 
 * 예약에 속한 이미지들을 표시하고 관리하는 컴포넌트
 * - 이미지 추가 (다중 업로드)
 * - 이미지 삭제
 * - 이미지 클릭 시 확대 보기
 */

import React, { useState } from 'react';
import heic2any from 'heic2any';
import {
    uploadReservationImages,
    deleteReservationImage,
    uploadManualReservationImages,
    deleteManualReservationImage
} from '../services/api';
import {
    getReservationImageEntryUrl,
    normalizeReservationImageUrl
} from '../utils/reservationImageUrl';

export const RESERVATION_IMAGE_KINDS = Object.freeze({
    REGULAR: 'regular',
    MANUAL: 'manual'
});

const RESERVATION_IMAGE_API_BY_KIND = Object.freeze({
    [RESERVATION_IMAGE_KINDS.REGULAR]: Object.freeze({
        upload: uploadReservationImages,
        remove: deleteReservationImage
    }),
    [RESERVATION_IMAGE_KINDS.MANUAL]: Object.freeze({
        upload: uploadManualReservationImages,
        remove: deleteManualReservationImage
    })
});

export function ManualReservationImageGallery(props) {
    return (
        <ReservationImageGallery
            {...props}
            reservationKind={RESERVATION_IMAGE_KINDS.MANUAL}
        />
    );
}

/**
 * ReservationImageGallery 컴포넌트
 * @param {Object} props
 * @param {number} props.reservationId - 예약 ID
 * @param {Array} props.images - 이미지 배열 [{id, image_url, display_order}]
 * @param {string} props.legacyImage - 기존 단일 이미지 URL (하위 호환성)
 * @param {boolean} props.isEditing - 수정 모드 여부
 * @param {boolean} props.disabled - 비활성화 여부
 * @param {string} props.lang - 언어 코드 (ko, en, fr)
 * @param {'regular'|'manual'} props.reservationKind - 이미지 소유 예약 유형 (API 경로 분기)
 * @param {Function} props.onImagesChange - 이미지 변경 시 콜백 (newImages) => void
 * @param {Function} props.onImageClick - 이미지 클릭 시 콜백 (imageUrl) => void
 * @param {Function} props.onError - 에러 발생 시 콜백 (errorMessage) => void
 */
export default function ReservationImageGallery({
    reservationId,
    images = [],
    legacyImage = '',
    isEditing = false,
    disabled = false,
    lang = 'ko',
    reservationKind,
    onImagesChange,
    onImageClick,
    onError
}) {
    const [uploading, setUploading] = useState(false);
    const imageApi = RESERVATION_IMAGE_API_BY_KIND[reservationKind];

    // 번역
    const TEXT = {
        addImage: { ko: '이미지 추가', en: 'Add image', fr: 'Ajouter une image' },
        removeImage: { ko: '삭제', en: 'Remove', fr: 'Supprimer' },
        uploadFailed: { ko: '이미지 업로드 실패', en: 'Upload failed', fr: 'Échec du téléchargement' },
        deleteFailed: { ko: '이미지 삭제 실패', en: 'Delete failed', fr: 'Échec de la suppression' }
    };

    const t = (key) => TEXT[key]?.[lang] || TEXT[key]?.ko || key;

    // 파일 업로드 처리
    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        try {
            setUploading(true);

            // 파일 전처리 (HEIC 변환)
            const processedFiles = [];
            for (let file of files) {
                if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
                    try {
                        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.95 });
                        file = new File([converted], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
                    } catch (err) {
                        console.error('HEIC 변환 실패:', err);
                        continue;
                    }
                }
                processedFiles.push(file);
            }

            if (processedFiles.length > 0) {
                if (!imageApi) {
                    throw new Error(`Unsupported reservation image kind: ${reservationKind || 'missing'}`);
                }

                const result = await imageApi.upload(reservationId, processedFiles);
                if (result.success && result.images) {
                    const newImages = [...images, ...result.images];
                    onImagesChange?.(newImages);
                }
            }
        } catch (error) {
            console.error('이미지 업로드 실패:', error);
            onError?.(t('uploadFailed'));
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    // 이미지 삭제 처리
    const handleDelete = async (imageId) => {
        if (!imageId) return;

        try {
            if (!imageApi) {
                throw new Error(`Unsupported reservation image kind: ${reservationKind || 'missing'}`);
            }

            await imageApi.remove(reservationId, imageId);
            const newImages = images.filter(img => img.id !== imageId);
            onImagesChange?.(newImages);
        } catch (error) {
            console.error('이미지 삭제 실패:', error);
            onError?.(t('deleteFailed'));
        }
    };

    // 이미지 클릭 처리
    const handleClick = (imageUrl) => {
        onImageClick?.(normalizeReservationImageUrl(imageUrl));
    };

    // 수정 모드 렌더링
    if (isEditing) {
        // images 배열이 비어있으면 legacyImage를 폴백으로 사용
        const editingImages = images.length > 0 ? images : (legacyImage ? [{ image_url: legacyImage, isLegacy: true }] : []);

        return (
            <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: 0, overflowX: 'auto', alignItems: 'flex-start', paddingBottom: '10px' }}>
                {/* + 추가 버튼 */}
                <label
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '60px',
                        height: '60px',
                        border: '1px dashed #ccc',
                        borderRadius: '4px',
                        cursor: disabled || uploading ? 'not-allowed' : 'pointer',
                        backgroundColor: uploading ? '#f0f0f0' : '#fafafa',
                        flexShrink: 0,
                        opacity: disabled || uploading ? 0.6 : 1
                    }}
                    title={t('addImage')}
                >
                    {uploading ? (
                        <span style={{ fontSize: '10px', color: '#999' }}>...</span>
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                    )}
                    <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/heic"
                        multiple
                        onChange={handleFileUpload}
                        disabled={disabled || uploading}
                        style={{ display: 'none' }}
                    />
                </label>

                {/* 이미지 갤러리 */}
                {editingImages.map((img, idx) => (
                    <div key={img.id || idx} style={{ position: 'relative', flexShrink: 0 }}>
                        <img
                            src={normalizeReservationImageUrl(getReservationImageEntryUrl(img))}
                            alt={`Attachment ${idx + 1}`}
                            style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                border: img.isLegacy ? '1px solid #ffa500' : '1px solid #ddd',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                            onClick={() => handleClick(getReservationImageEntryUrl(img))}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {/* 삭제 버튼 - 레거시 이미지는 삭제 불가 (아직 마이그레이션 안됨) */}
                        {!img.isLegacy && (
                            <button
                                type="button"
                                onClick={() => handleDelete(img.id)}
                                style={{
                                    position: 'absolute',
                                    bottom: '-6px',
                                    right: '-6px',
                                    width: '18px',
                                    height: '18px',
                                    padding: '0',
                                    backgroundColor: '#dc3545',
                                    border: 'none',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                title={t('removeImage')}
                            >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                </svg>
                            </button>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    // 읽기 모드 렌더링
    const displayImages = images.length > 0 ? images : (legacyImage ? [{ image_url: legacyImage }] : []);

    return (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {displayImages.map((img, idx) => (
                <img
                    key={img.id || idx}
                    src={normalizeReservationImageUrl(getReservationImageEntryUrl(img))}
                    alt={`Attachment ${idx + 1}`}
                    style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'cover',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                    onClick={() => handleClick(getReservationImageEntryUrl(img))}
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            ))}
        </div>
    );
}
